import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHead } from "./AdminLayout";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "./ImageUpload";
import TranslateDialog, { TranslateButton } from "./TranslateDialog";
import { imgUrl } from "@/context/SiteContext";

const SCHEMA = {
  services: { label: "Layanan", cols: ["title", "slug"], fields: [["title", "Judul"], ["slug", "Slug"], ["icon", "Ikon Lucide (mis. Building2)"], ["short", "Ringkasan", "area"], ["description", "Deskripsi", "area"], ["features", "Fitur (1 per baris)", "list"], ["order", "Urutan", "number"]] },
  portfolio: { label: "Portofolio", cols: ["title", "client"], fields: [["image", "Foto Proyek", "image"], ["title", "Judul Proyek"], ["client", "Klien"], ["category", "Kategori (bebas, mis. SBU, SKK, ISO)"], ["location", "Lokasi"], ["year", "Tahun"], ["result", "Hasil"], ["order", "Urutan Tampil (kecil = awal)", "number"], ["visible", "Tampilkan di website", "bool"], ["description", "Deskripsi", "area"]] },
  articles: { label: "Artikel / Blog", cols: ["title", "category"], fields: [["title", "Judul"], ["slug", "Slug"], ["category", "Kategori"], ["cover", "Gambar Cover", "image"], ["read_time", "Menit baca", "number"], ["excerpt", "Ringkasan", "area"], ["content", "Isi Artikel", "long"], ["published", "Terbitkan", "bool"]] },
  faqs: { label: "FAQ", cols: ["question", "category"], fields: [["question", "Pertanyaan"], ["answer", "Jawaban", "area"], ["category", "Kategori"], ["order", "Urutan", "number"]] },
};

const inputCls = "mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#D97706]";

function ItemForm({ kind, item, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF(item || {});
  }, [item]);
  const save = async () => {
    try {
      const body = { ...f };
      SCHEMA[kind].fields.forEach(([k, , t]) => { if (t === "number") body[k] = Number(body[k] || 0); });
      const { data } = item?.id ? await api.put(`/admin/content/${kind}/${item.id}`, body) : await api.post(`/admin/content/${kind}`, body);
      toast.success("Tersimpan sebagai draft — klik Publish ke Website untuk menayangkan");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none" data-testid="content-form-dialog">
        <DialogHeader><DialogTitle>{item?.id ? "Edit" : "Tambah"} {SCHEMA[kind].label}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {SCHEMA[kind].fields.map(([k, label, t]) => (
            <label key={k} className={`block text-xs font-semibold text-slate-600 ${["area", "long", "list", "image"].includes(t) ? "sm:col-span-2" : ""}`}>
              {label}
              {t === "image" ? <ImageUpload value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} testid={`content-field-${k}`} />
                : t === "bool" ? <input type="checkbox" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} className="ml-2" data-testid={`content-field-${k}`} />
                : t === "list" ? <textarea rows={4} value={(f[k] || []).join("\n")} onChange={(e) => setF({ ...f, [k]: e.target.value.split("\n") })} className={inputCls} data-testid={`content-field-${k}`} />
                : t === "area" || t === "long" ? <textarea rows={t === "long" ? 10 : 3} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`content-field-${k}`} />
                : <input type={t === "number" ? "number" : "text"} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`content-field-${k}`} />}
            </label>
          ))}
        </div>
        <button onClick={save} className="mt-2 bg-[#0F2B48] py-3 text-sm font-bold text-white hover:bg-[#D97706]" data-testid="content-save-button">Simpan</button>
      </DialogContent>
    </Dialog>
  );
}

export default function ContentManager() {
  const [kind, setKind] = useState("services");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [translating, setTranslating] = useState(null);
  const [trFields, setTrFields] = useState({});
  useEffect(() => { api.get(`/admin/content/${kind}`).then((r) => setItems(kind === "portfolio" ? [...r.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) : r.data)); }, [kind]);
  useEffect(() => { api.get("/admin/translatable").then((r) => setTrFields(r.data.fields)); }, []);
  const [job, setJob] = useState(null);
  useEffect(() => {
    if (!job?.running) return undefined;
    const id = setInterval(async () => {
      const { data } = await api.get("/admin/translate-all");
      setJob(data);
      if (!data.running) {
        toast.success(`Terjemahan selesai: ${data.done - data.failed}/${data.total} konten`);
        api.get(`/admin/content/${kind}`).then((r) => setItems(r.data));
      }
    }, 4000);
    return () => clearInterval(id);
  }, [job?.running, kind]);
  const translateAll = async () => {
    const { data } = await api.post("/admin/translate-all");
    setJob(data);
    if (!data.total) toast.info("Semua konten sudah memiliki terjemahan EN & 中文");
  };
  const toggleVisible = async (i) => {
    const { data } = await api.put(`/admin/content/portfolio/${i.id}`, { visible: i.visible === false });
    onSaved(data);
  };
  const del = async (id) => {
    if (!window.confirm("Hapus item ini?")) return;
    await api.delete(`/admin/content/${kind}/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
    toast.success("Dihapus");
  };
  const onSaved = (d) => {
    setItems((x) => (x.some((i) => i.id === d.id) ? x.map((i) => (i.id === d.id ? d : i)) : [d, ...x]));
    setEditing(null);
  };
  const [c1, c2] = SCHEMA[kind].cols;
  return (
    <div data-testid="admin-content">
      <PageHead eyebrow="CMS" title="Manajemen Konten" desc="Kelola layanan, portofolio, artikel/blog, dan FAQ yang tampil di website. Profil & landing page diatur di menu Profil Perusahaan.">
        <button onClick={translateAll} disabled={job?.running} className="inline-flex items-center gap-2 border border-[#0284C7] bg-white px-4 py-2.5 text-sm font-bold text-[#0284C7] hover:bg-sky-50 disabled:opacity-60" data-testid="content-translate-all-button">
          <Sparkles size={16} /> {job?.running ? `Menerjemahkan ${job.done}/${job.total}...` : "Terjemahkan Semua (AI)"}
        </button>
        <button onClick={() => setEditing(kind === "portfolio" ? { visible: true, order: items.length + 1 } : { published: true })} className="inline-flex items-center gap-2 bg-[#D97706] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#B45309]" data-testid="content-add-button"><Plus size={16} /> Tambah {SCHEMA[kind].label}</button>
      </PageHead>
      <Tabs value={kind} onValueChange={setKind}>
        <TabsList className="mb-4 rounded-none">{Object.entries(SCHEMA).map(([k, s]) => <TabsTrigger key={k} value={k} className="rounded-none" data-testid={`content-tab-${k}`}>{s.label}</TabsTrigger>)}</TabsList>
      </Tabs>
      <div className="divide-y border border-slate-200 bg-white">
        {items.map((i) => (
          <div key={i.id} className="flex items-center justify-between gap-4 p-4" data-testid={`content-row-${i.id}`}>
            <div className="flex min-w-0 items-center gap-3">
              {(i.image || i.cover) && <img src={imgUrl(i.image || i.cover)} alt="" className="h-12 w-16 shrink-0 object-cover" />}
              <div className="min-w-0">
                <p className="truncate font-semibold text-[#0F2B48]">{kind === "portfolio" && <span className="mr-2 font-mono text-xs text-slate-400">#{i.order ?? 0}</span>}{i[c1]}</p>
                <p className="text-xs text-slate-500">{i[c2]} {kind === "articles" && (i.published ? "· Terbit" : "· Draft")} {i.category && kind === "portfolio" ? `· ${i.category}` : ""} {i.i18n?.en ? "· EN ✓" : ""} {i.i18n?.zh ? "· 中文 ✓" : ""}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {kind === "portfolio" && <label className="mr-2 flex items-center gap-1.5 text-[11px] text-slate-500">Tampil <Switch checked={i.visible !== false} onCheckedChange={() => toggleVisible(i)} data-testid={`portfolio-visible-${i.id}`} /></label>}
              <TranslateButton onClick={() => setTranslating(i)} testid={`content-translate-${i.id}`} />
              <button onClick={() => setEditing(i)} className="border p-2 hover:bg-slate-50" data-testid={`content-edit-${i.id}`}><Pencil size={14} /></button>
              <button onClick={() => del(i.id)} className="border p-2 text-rose-600 hover:bg-rose-50" data-testid={`content-delete-${i.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="p-8 text-center text-sm text-slate-500">Belum ada data.</p>}
      </div>
      <ItemForm kind={kind} item={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      <TranslateDialog open={!!translating} onClose={() => setTranslating(null)} kind={kind} item={translating} fields={trFields[kind] || []}
        onSaved={(i18n) => { setItems((x) => x.map((it) => (it.id === translating?.id ? { ...it, i18n } : it))); setTranslating((tr) => tr && { ...tr, i18n }); }} />
    </div>
  );
}
